import { multiUploadTask } from '@/api/upload.js';
import Uploader from '@/utils/upload.js';
import { captureVideoFrame } from '@/utils/media.js';
import OnlyAudioLogo from '@/assets/recording/only-audio.jpg';
import adapter from 'webrtc-adapter';
import SentryTracker from '@/utils/Sentry/sentryUtils.js';

function getSupportedVideoMimeType() {
  const types = ['video/mp4'];
  let result = 'video/webm';
  for (let i = 0; i < types.length; i++) {
    const type = types[i];
    if (MediaRecorder.isTypeSupported(type)) {
      result = type;
      break;
    }
  }
  return result;
}

const STREAM_TYPE = {
  VIDEO: 'VIDEO',
  AUDIO: 'AUDIO',
};
const MIME_TYPES = {
  [STREAM_TYPE.VIDEO]: getSupportedVideoMimeType(),
  [STREAM_TYPE.AUDIO]: 'audio/webm',
};
const RECORD_OPTIONS = {
  [STREAM_TYPE.VIDEO]: {
    mimeType: MIME_TYPES[STREAM_TYPE.VIDEO],
    videoBitsPerSecond: 10 * 1024 * 1024,
  },
  [STREAM_TYPE.AUDIO]: {
    mimeType: MIME_TYPES[STREAM_TYPE.AUDIO], // 业务应标明，但safari存在兼容问题，设置此参数导致webm转换wav异常
  },
};
const RECORD_TIME_SLICE = 5 * 1000;
const MINIMUM_CHUNK_SIZE = 5 * 1024 * 1024;

const judgeStreamType = (key) =>
  ['camera', 'screen', 'file'].includes(key) ? STREAM_TYPE.VIDEO : STREAM_TYPE.AUDIO;

function generateUploadParams(streams) {
  const result = {
    streams: [],
    thumbnail: true,
  };
  streams.forEach(([key]) => {
    const type = judgeStreamType(key);
    result.streams.push({
      ext: MIME_TYPES[type].split('/')[1],
      mimeType: MIME_TYPES[type],
    });
  });
  return result;
}

const isChunkOverMiniSize = (chunk) => chunk.size > MINIMUM_CHUNK_SIZE;

function getProjectStreamType(key) {
  return {
    microphone: 0,
    screen: 1,
    file: 1,
    camera: 2,
  }[key];
}

function getProjectStreamZIndex(key) {
  return {
    microphone: 0,
    screen: 1,
    file: 1,
    camera: 2,
  }[key];
}

let previewImage;
async function generatePreviewImage(
  uploadId,
  mainStream,
  sideStream,
  options = {
    mainStreamMirror: false,
    sideStreamMirror: false,
  },
) {
  const uploader = new Uploader(0);
  uploader.setUploadId(uploadId);
  if (mainStream) {
    const promiseArray = [];
    [mainStream, sideStream].forEach((stream) => {
      if (stream) {
        promiseArray.push(
          new Promise((resolve) => {
            const video = document.createElement('video');
            video.muted = true;
            video.autoplay = true;
            video.addEventListener(
              'canplay',
              () => {
                resolve(video);
              },
              {
                once: true,
              },
            );
            video.srcObject = stream;
            video.play();
          }),
        );
      }
    });

    const [mainVideo, sideVideo] = await Promise.all(promiseArray);
    const imageBlob = await captureVideoFrame(mainVideo, sideVideo, {
      mainVideoMirror: options.mainStreamMirror,
      sideVideoMirror: options.sideStreamMirror,
    });
    await uploader.uploadBlobSlice(imageBlob);
    const uploadInfo = await uploader.complete();
    [mainVideo, sideVideo].forEach((video) => {
      if (video) {
        video.srcObject = null;
        video.load();
        video = null;
      }
    });
    return uploadInfo;
  } else {
    const response = await fetch(OnlyAudioLogo);
    const imageBlob = await response.blob();
    await uploader.uploadBlobSlice(imageBlob);
    return uploader.complete();
  }
}

let recorderCollection = [];
export async function startRecordMedia(
  streams = {
    camera: null,
    microphone: null,
    screen: null,
    file: null,
  },
  options = {
    cameraMirror: false,
  },
) {
  disposeRecordMedia();
  previewImage = null;
  const availableStreams = Object.entries(streams).filter((entry) => entry[1]);
  if (availableStreams.length === 0) {
    throw new Error('No stream available');
  }

  const uploadParams = generateUploadParams(availableStreams);
  const res = await multiUploadTask(uploadParams);
  if (res?.code !== 0) {
    throw new Error(`Failed to create multipart upload task`);
  }

  const { uploadIds } = res.data;
  availableStreams.forEach(([key, stream], index) => {
    const type = judgeStreamType(key);
    const params = RECORD_OPTIONS[type];
    const recorder =
      adapter.browserDetails.browser !== 'safari'
        ? new MediaRecorder(stream, params)
        : new MediaRecorder(stream);
    const uploadId = uploadIds[index];
    const uploader = new Uploader(0, null, { showMessage: true });
    uploader.setUploadId(uploadId);
    const collection = {
      key,
      mimeType: MIME_TYPES[type],
      recorder,
      blob: new Blob([]),
      toUpload: new Blob([]),
      uploadId,
      uploader,
      pendingUploadTask: null,
      isMirrorOpen: false,
    };
    if (key === 'camera') {
      collection.isMirrorOpen = options.cameraMirror;
    }
    recorderCollection.push(collection);
    recorder.addEventListener('dataavailable', (e) => {
      const chunk = e.data;
      collection.blob = new Blob([collection.blob, chunk]);
      collection.toUpload = new Blob([collection.toUpload, chunk]);
      SentryTracker.captureDebug('dataavailable:', key, chunk.size);

      if (isChunkOverMiniSize(collection.toUpload)) {
        collection.pendingUploadTask = uploader.uploadBlobSlice(collection.toUpload);
        collection.toUpload = new Blob([]);
      }
    });
    recorder.onerror = (e) => {
      SentryTracker.captureDebug('recorder error:', key, e.message);
    };

    recorder.start(RECORD_TIME_SLICE);
  });

  let mainStream, sideStream;
  let mainStreamMirror = false,
    sideStreamMirror = false;
  availableStreams.forEach(([key, stream]) => {
    if (['screen', 'file'].includes(key)) {
      mainStream = stream;
    } else if (key === 'camera') {
      sideStream = stream;
      sideStreamMirror = options.cameraMirror;
    }
  });
  if (!mainStream && sideStream) {
    mainStream = sideStream;
    mainStreamMirror = sideStreamMirror;
    sideStream = null;
    sideStreamMirror = false;
  }
  previewImage = generatePreviewImage(uploadIds.at(-1), mainStream, sideStream, {
    mainStreamMirror,
    sideStreamMirror,
  });
}

export function pauseRecordMedia() {
  recorderCollection.forEach((collection) => {
    collection.recorder.pause();
  });
}

export function resumeRecordMedia() {
  recorderCollection.forEach((collection) => {
    collection.recorder.resume();
  });
}

export async function stopRecordMedia() {
  const completePromiseArray = [];
  recorderCollection.forEach((collection) => {
    const { recorder } = collection;
    completePromiseArray.push(
      new Promise((resolve, reject) => {
        recorder.addEventListener('stop', async () => {
          SentryTracker.captureDebug('stop event:', collection.key);
          const { key, uploader, pendingUploadTask, toUpload, blob, isMirrorOpen } = collection;
          collection.previewUrl = URL.createObjectURL(blob);
          if (pendingUploadTask) {
            await pendingUploadTask;
          }
          if (toUpload.size > 0) {
            await uploader.uploadBlobSlice(toUpload);
          }
          const { uploadId, eTags } = await uploader.complete().catch(reject);
          resolve({
            type: getProjectStreamType(key),
            zIndex: getProjectStreamZIndex(key),
            upload: {
              uploadId,
              completedParts: eTags,
            },
            isMirrorOpen,
          });
        });
        recorder.stop();
        SentryTracker.captureDebug('stop record:', collection.key);
      }),
    );
  });
  const streamsUploadInfo = await Promise.all(completePromiseArray).catch((e) => {
    throw e;
  });
  const previewUploadInfo = await previewImage;

  return {
    name: generateProjectName('Recording'),
    thumbnailUpload: {
      uploadId: previewUploadInfo.uploadId,
      completedParts: previewUploadInfo.eTags,
    },
    streams: streamsUploadInfo,
    type: 0,
  };
}

export function getPreviewMediaUrl() {
  let leftURL;
  let leftMirror = false;
  let rightURL;
  let rightMirror = false;
  let audioURL;
  recorderCollection.forEach(({ key, previewUrl, isMirrorOpen }) => {
    if (['screen', 'file'].includes(key)) {
      leftURL = previewUrl;
      leftMirror = isMirrorOpen;
    } else if (key === 'camera') {
      rightURL = previewUrl;
      rightMirror = isMirrorOpen;
    } else if (key === 'microphone') {
      audioURL = previewUrl;
    }
  });

  if (!leftURL) {
    leftURL = rightURL;
    leftMirror = rightMirror;
    rightURL = null;
    rightMirror = false;
  }

  return {
    leftURL,
    leftMirror,
    rightURL,
    rightMirror,
    audioURL,
  };
}

export function disposeRecordMedia() {
  recorderCollection.forEach((collection) => {
    Object.keys(collection).forEach((key) => {
      if (key === 'previewUrl') {
        URL.revokeObjectURL(collection[key]);
      }
      collection[key] = null;
    });
  });
  recorderCollection = [];
}

export function generateProjectName(type) {
  const today = new Date().toDateString();
  const [month, day, year] = today.split(' ').slice(1);
  return `${type} - ${month} ${day}, ${year}`;
}

const completedRecordInfo = {};
export function setCompletedRecordInfo(name, time) {
  completedRecordInfo.name = name;
  completedRecordInfo.time = time;
}
export function getCompletedRecordInfo() {
  return {
    ...completedRecordInfo,
  };
}

export class SingleStreamRecorder {
  #recorder;

  constructor(stream) {
    this.#recorder = new MediaRecorder(stream);
  }

  start() {
    this.#recorder.start();
  }

  pause() {
    this.#recorder.pause();
  }

  stop() {
    return new Promise((resolve) => {
      this.#recorder.addEventListener('dataavailable', (e) => {
        resolve(e.data);
      });
      this.#recorder.stop();
    });
  }
}
